import React, { Component } from "react";
import { observer } from "mobx-react";

import { fork } from "@formant/core/common/fork";
import { duration } from "@formant/core/model/duration";
import {
    ITeleopHighPingReconnectBehaviors,
    teleopHighPingReconnectBehaviorsDefaults,
} from "@formant/core/model/ITeleopHighPingReconnectBehaviors";
import { IPingInfo } from "@formant/rtc-client/models/IPingInfo";
import { IRtcConnectionStatsInfo } from "@formant/rtc-client/models/IRtcConnectionStatsInfo";

import { track } from "../../../analytics";
import { appStore, operateStore, organizationStore } from "../../../stores";
import { Timer } from "../../common/Timer";
import { PulseItem } from "./PulseItem";

export interface ITeleopLatencyIndicatorProps {
    disableHighPingWarning?: boolean;
}

@observer
export class TeleopLatencyIndicator extends Component<ITeleopLatencyIndicatorProps> {
    pingWasHigh = false;
    pingLoss = 0;
    pings: number[] = [];
    pingInfo: IPingInfo | undefined;
    connectionStats: IRtcConnectionStatsInfo | undefined;
    lastConnectionStatsRefresh: number | undefined;
    txThroughputMbps: number | undefined;
    rxThroughputMbps: number | undefined;
    interval = duration.second;
    warningPeriods: number = 0;
    disconnectPeriods: number = 0;
    settings: ITeleopHighPingReconnectBehaviors =
        teleopHighPingReconnectBehaviorsDefaults;

    public constructor(props: ITeleopLatencyIndicatorProps) {
        super(props);

        this.settings = organizationStore.getTeleopLatencySettings();
    }
    public render() {
        const { session } = operateStore;
        if (!session) {
            return false;
        }
        const { pingInfo } = this;
        const { ping } = session;

        const localCandidateIP = this.connectionStats?.localCandidate?.address;
        const remoteCandidateIP =
            this.connectionStats?.remoteCandidate?.address;

        const lastMessageTimestamp = session.getLastMessageTimestamp();
        const lastHeard = lastMessageTimestamp
            ? new Date(lastMessageTimestamp).toISOString()
            : undefined;

        const { pingsSent, pongsReceived } = pingInfo || {
            pingsSent: 0,
            pongsReceived: 0,
        };

        if (pingsSent > 0) {
            const notRecieved = pingsSent - pongsReceived;
            this.pingLoss = notRecieved / pingsSent;
        } else {
            this.pingLoss = 0;
        }

        let tooltip = ``;
        if (pingInfo) {
            tooltip += `Pings: ${pingInfo.pingsSent}\n`;
            tooltip += `Pongs: ${pingInfo.pongsReceived}\n`;
            tooltip += `Loss: ${(pingInfo.loss * 100).toFixed(1)}%\n`;
            tooltip += `Average: ${pingInfo?.average.toFixed(0)} ms\n`;
            tooltip += `Min: ${pingInfo.min} ms\n`;
            tooltip += `Max: ${pingInfo.max} ms\n`;
            tooltip += `Jitter (std dev): ${pingInfo?.standardDeviation.toFixed(
                0,
            )} ms\n`;
        }
        tooltip += `TX to Device: ${
            this.txThroughputMbps
                ? this.txThroughputMbps.toFixed(2) + " Mbps"
                : "..."
        }\n`;
        tooltip += `RX from Device: ${
            this.rxThroughputMbps
                ? this.rxThroughputMbps.toFixed(2) + " Mbps"
                : "..."
        }`;
        if (localCandidateIP) {
            tooltip += `\nLocal IP: ${localCandidateIP}`;
        }
        if (remoteCandidateIP) {
            tooltip += `\nDevice IP: ${remoteCandidateIP}`;
        }
        tooltip += `${lastHeard ? `\nLast pong: ${lastHeard}` : ""}`;

        return (
            <>
                <Timer onTick={this.onGetPing} interval={this.interval} />
                <PulseItem
                    label="PING"
                    valueSuffix="ms"
                    value={ping}
                    min={0}
                    max={400}
                    tooltip={tooltip}
                    data-testid="ping-info"
                />
            </>
        );
    }

    private fail = () => {
        const { session } = operateStore;
        const { shouldRetry } = this.settings;
        if (!session) {
            return false;
        }

        if (session.hasStopped) {
            return;
        }

        if (shouldRetry) {
            fork(session.restart());
        } else {
            session.hasStopped = true;
            fork(session.restart());
        }
    };

    private failOnHighPingLoss = () => {
        const { session } = operateStore;
        const { lossThreshold, highLossPeriods } = this.settings;
        if (!session) {
            return false;
        }
        if (session.hasStopped) {
            return;
        }
        this.fail();
        track("Teleop Session Stopped Due To Ping Loss", {
            "Session ID": session.sessionId,
            "Teleop Session ID": session.uuid,
            Ping: session.ping,
            "Ping Loss": this.pingLoss,
        });
        appStore.setSnackbar(
            `Stopped session:  ping loss > ${lossThreshold.toString()} for ${highLossPeriods} seconds`,
        );
        this.disconnectPeriods = 0;
        this.warningPeriods = 0;
    };

    private failOnHighPing = () => {
        const { session } = operateStore;
        const { maxPingDisconnectThreshold } = this.settings;
        if (!session) {
            return false;
        }
        this.fail();
        track("Teleop Session Stopped Due To High Ping", {
            "Session ID": session.sessionId,
            "Teleop Session ID": session.uuid,
            Ping: session.ping,
            "Ping Loss": this.pingLoss,
        });
        appStore.setSnackbar(
            `Stopped session: Ping > ${maxPingDisconnectThreshold.toString()}ms, for ${
                this.disconnectPeriods
            } seconds`,
        );
        this.disconnectPeriods = 0;
        this.warningPeriods = 0;
    };

    private onGetPing = async () => {
        const { session, share } = operateStore;
        const {
            highLossPeriods,
            maxHighPingWarningPeriods,
            maxPingDisconnectPeriods,
            maxPingDisconnectThreshold,
            maxPingThreshold,
            recoveryThreshhold,
            lossThreshold,
        } = this.settings;
        if (!session) {
            return false;
        }
        this.pingInfo = session.getPingInfo();

        const freshConnectionStats = await session.getConnectionStatsInfo();

        if (
            this.connectionStats &&
            this.lastConnectionStatsRefresh &&
            freshConnectionStats
        ) {
            // if we have a previous measurement, we can calculate tx and rx mbps
            const timeElapsedMilliseconds =
                Date.now() - this.lastConnectionStatsRefresh;
            const txDeltaBits =
                (freshConnectionStats.candidatePair.bytesSent -
                    this.connectionStats.candidatePair.bytesSent) *
                8;
            const rxDeltaBits =
                (freshConnectionStats.candidatePair.bytesReceived -
                    this.connectionStats.candidatePair.bytesReceived) *
                8;

            if (isNaN(rxDeltaBits) || isNaN(rxDeltaBits)) {
                this.txThroughputMbps = undefined;
                this.rxThroughputMbps = undefined;
            } else {
                // Convert milliseconds to seconds
                const secondsElapsed = timeElapsedMilliseconds / 1000;

                // Calculate throughput in megabits per second (Mbps)
                this.txThroughputMbps = txDeltaBits / secondsElapsed / 1e6;
                this.rxThroughputMbps = rxDeltaBits / secondsElapsed / 1e6;
            }
        } else {
            this.txThroughputMbps = undefined;
            this.rxThroughputMbps = undefined;
        }

        this.connectionStats = freshConnectionStats;
        this.lastConnectionStatsRefresh = Date.now();

        await session.getPing();
        if (session.ping) {
            this.pings.push(session.ping);
            if (this.pings.length > 10) {
                const pings = this.pings;
                this.pings = [];
                const average = pings.reduce((a, b) => a + b) / pings.length;
                const minPing = Math.min(...pings);
                const maxPing = Math.max(...pings);
                track("Teleop Ping", {
                    "Average Ping": average,
                    "Max Ping": maxPing,
                    "Min Ping": minPing,
                    "Session ID": session.sessionId,
                    "Teleop Session ID": session.uuid,
                });
            }
        }
        if ((!share && this.pingInfo?.pongsReceived) ?? 0 > 10) {
            if (this.settings.enabled) {
                const message = `We've detected a connectivity problem.`;
                if (
                    (session.ping || 0) > maxPingThreshold &&
                    !this.pingWasHigh
                ) {
                    if (this.warningPeriods >= maxHighPingWarningPeriods) {
                        if (!this.props.disableHighPingWarning) {
                            appStore.setSnackbar(
                                `${message} Ping was > ${maxPingThreshold.toString()}ms, for ${
                                    this.warningPeriods
                                } seconds`,
                            );
                        }
                        // we've warned them enough, now let's go into the kill loop
                        this.pingWasHigh = true;
                        this.warningPeriods = 0;
                    }
                    this.warningPeriods++;
                }
                if (this.pingWasHigh) {
                    if ((session.ping || 0) > maxPingDisconnectThreshold) {
                        if (
                            this.disconnectPeriods >= maxPingDisconnectPeriods
                        ) {
                            if (!this.props.disableHighPingWarning) {
                                appStore.setSnackbar(
                                    `${message} Ping > ${maxPingDisconnectThreshold.toString()}ms, for ${
                                        this.disconnectPeriods
                                    } seconds`,
                                );
                            }
                            this.failOnHighPing();
                        }
                        this.disconnectPeriods++;
                    } else {
                        // recover
                        this.disconnectPeriods = 0;
                        if ((session.ping || 0) < recoveryThreshhold) {
                            this.pingWasHigh = false;
                        }
                    }
                }
                if (this.pingLoss > lossThreshold) {
                    if (this.warningPeriods >= highLossPeriods) {
                        this.failOnHighPingLoss();
                    }
                    this.warningPeriods++;
                }
            }
        }
    };
}
